import { useState, useEffect, useCallback, useRef } from 'react';
import { blink } from '@/lib/blink';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { requestThrottler, BatchProcessor } from '@/lib/requestThrottler';
import { Top50DashboardSkeleton, Top50ContactCardSkeleton } from '@/components/ui/data-skeleton';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { contactIntelligenceEngine, type ContactCard, type Top50Dashboard as CI_Top50Dashboard } from '@/services/contactIntelligenceEngine';
type WeeklyTouchPlan = CI_Top50Dashboard['weeklyTouchPlan'][number];
import {
  calculatePAMSSACScore,
  aggregatePersonProfile,
  type PAMSSACScore,
  type PersonProfile,
} from '@/services/pamssacScoringEngineV2';
import { safeJsonParse } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  AlertTriangle,
  Zap,
  Crown,
  Activity,
  Calendar,
  Mail,
  RefreshCw,
  Sparkles,
  ArrowRight,
  Filter,
  ArrowUpDown
} from 'lucide-react';

interface DashboardData {
  relationships: ContactCard[];
  weeklyTouchPlan: WeeklyTouchPlan[];
  powerNodes: ContactCard[];
  decayingRelationships: ContactCard[];
}

interface EnhancedContactCard extends ContactCard {
  pamssac?: PAMSSACScore;
  profile?: PersonProfile;
}

export default function Top50Dashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [filterReady, setFilterReady] = useState(false);
  const [sortBy, setSortBy] = useState<'ci' | 'motivation' | 'access' | 'suitability'>('ci');
  const [displayedContacts, setDisplayedContacts] = useState<EnhancedContactCard[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const contactsPerBatch = 10;
  
  // Refs to capture latest values in loadDashboard callback
  const sortByRef = useRef(sortBy);
  const filterReadyRef = useRef(filterReady);
  
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);
  
  useEffect(() => {
    filterReadyRef.current = filterReady;
  }, [filterReady]);

  const loadDashboard = useCallback(async (userId: string, skipLoadingState = false) => {
    if (!skipLoadingState) {
      setLoading(true);
    }
    try {
      console.log('🔍 [TOP50 TRACE] Step 1: Starting loadDashboard for user:', userId);
      console.log('🔍 [TOP50 TRACE] Step 2: User ID type:', typeof userId, 'Value:', userId);
      
      // Fetch top 50 contacts from unified CI table with fallback
      console.log('🔍 [TOP50 TRACE] Step 3: Calling requestThrottler.enqueue');
      let contacts: any[] = [];
      
      try {
        // Try unified table first
        contacts = await requestThrottler.enqueue(
          `contacts-${userId}-${sortByRef.current}-${filterReadyRef.current}`,
          async () => {
            console.log('🔍 [TOP50 TRACE] Step 4: Trying contactIntelligenceUnified');
            try {
              const result = await blink.db.contactIntelligenceUnified.list({
                where: { userId: userId },
                orderBy: { overallScore: 'desc' },
                limit: 50,
              });
              console.log('✅ Unified table returned:', result?.length || 0, 'contacts');
              return result || [];
            } catch (unifiedError) {
              console.warn('⚠️ Unified table failed, trying regular contacts table');
              // Fallback to regular contacts
              const result = await blink.db.contacts.list({
                where: { userId },
                limit: 50,
                orderBy: { aiRelationshipScore: 'desc' }
              });
              console.log('✅ Regular contacts table returned:', result?.length || 0, 'contacts');
              return result || [];
            }
          },
          { cacheTime: 30000, priority: 2 } // Cache for 30s
        );
      } catch (e) {
        console.error('❌ All contact queries failed:', e);
        contacts = [];
      }
      
      console.log('🔍 [TOP50 TRACE] Step 5: Query returned. Type:', typeof contacts, 'Array?', Array.isArray(contacts));
      console.log('🔍 [TOP50 TRACE] Step 6: Number of contacts:', contacts?.length || 0);
      console.log('🔍 [TOP50 TRACE] Step 7: First contact sample:', contacts?.[0] || 'NO CONTACTS');

      // Map unified contacts to ContactCard format
      const scoredContacts: EnhancedContactCard[] = [];
      
      // Map unified CI data directly (already scored)
      if (contacts.length > 0) {
        for (const unified of contacts) {
          // Handle both unified table and regular contacts table
          const contactEmail = unified.contactEmail || unified.email;
          const overallScore = Number(unified.overallScore) || Number(unified.aiRelationshipScore) || 0;
          const P = Number(unified.proximity) || 50;
          const A1 = Number(unified.affinity) || 50;
          const M = Number(unified.mutuality) || 50;
          const S1 = Number(unified.status) || 50;
          const S2 = Number(unified.similarity) || 50;
          const A2 = Number(unified.availability) || 50;
          const C = Number(unified.complementarity) || 50;
          
          // Sort dimensions by score to find weakest two
          const dimensions = [
            { dimension: 'P', score: P, evidence: ['Low proximity score'] },
            { dimension: 'A1', score: A1, evidence: ['Low affinity'] },
            { dimension: 'M', score: M, evidence: ['Low mutuality'] },
            { dimension: 'S1', score: S1, evidence: ['Low status match'] },
            { dimension: 'S2', score: S2, evidence: ['Low similarity'] },
            { dimension: 'A2', score: A2, evidence: ['Low availability'] },
            { dimension: 'C', score: C, evidence: ['Low complementarity'] }
          ].sort((a, b) => a.score - b.score);
          
          const card: EnhancedContactCard = {
            contactId: unified.personId || unified.id,
            name: unified.name || undefined,
            emails: [contactEmail].filter(Boolean),
            ciScore: {
              tier: overallScore >= 80 ? 'A' : overallScore >= 60 ? 'B' : 'C',
            },
            scores: {
              ci: overallScore,
              strength: Number(unified.relationshipHealth) || 0,
              relevance: Number(unified.strategicValue) || 0,
              influence: Number(unified.engagementLevel) || 0,
              responsiveness: Number(unified.trustLevel) || 0,
              reciprocity: 50, // Would calculate
              tier: overallScore >= 80 ? 'A' : overallScore >= 60 ? 'B' : 'C',
            },
            whyNow: unified.whyImportant ? [String(unified.whyImportant)] : ['Active relationship'],
            lastTouchAt: unified.lastInteractionAt,
            nextTouchDueAt: undefined, // Would calculate
            openLoops: unified.openCommitments ? safeJsonParse(unified.openCommitments || '[]') : [],
            sparkline: [],
            pamssac: {
              P, A1, M, S1, S2, A2, C,
              total: overallScore,
              weakestTwo: dimensions.slice(0, 2)
            }
          };
          scoredContacts.push(card);
        }
      }
      
      // LEGACY: Continue scoring for contacts not in unified table
      const legacyScoredContacts: EnhancedContactCard[] = [];

      console.log('🔍 [TOP50 TRACE] Step 8: Mapped contacts:', scoredContacts.length);
      console.log('🔍 [TOP50 TRACE] Step 9: First mapped contact:', scoredContacts[0] || 'NO MAPPED CONTACTS');
      
      // Sort by selected metric
      const currentSortBy = sortByRef.current;
      if (currentSortBy === 'motivation' && scoredContacts.some(c => c.pamssac)) {
        scoredContacts.sort((a, b) => (b.pamssac?.M || 0) - (a.pamssac?.M || 0));
      } else if (currentSortBy === 'access' && scoredContacts.some(c => c.pamssac)) {
        scoredContacts.sort((a, b) => (a.pamssac?.A1 || 100) - (b.pamssac?.A1 || 100)); // Lower access first
      } else if (currentSortBy === 'suitability' && scoredContacts.some(c => c.pamssac)) {
        scoredContacts.sort((a, b) => (b.pamssac?.S1 || 0) - (a.pamssac?.S1 || 0));
      } else {
        scoredContacts.sort((a, b) => b.scores.ci - a.scores.ci);
      }

      // Filter if "Ready to mobilize" is selected
      let filteredContacts = scoredContacts;
      const currentFilterReady = filterReadyRef.current;
      if (currentFilterReady) {
        filteredContacts = scoredContacts.filter(c => c.pamssac && c.pamssac.total >= 75);
      }

      // Top 50
      const top50 = filteredContacts.slice(0, 50);

      // Power nodes (Tier A with high influence)
      const powerNodes = top50.filter(
        c => c.scores.tier === 'A' && c.scores.influence >= 70
      );

      // Decaying relationships (high score but no recent activity)
      const decayingRelationships = top50.filter(c => {
        if (!c.lastTouchAt) return false;
        const daysSinceTouch = (Date.now() - new Date(c.lastTouchAt).getTime()) / (1000 * 60 * 60 * 24);
        return c.scores.ci >= 70 && daysSinceTouch > 30;
      });

      // Generate weekly touch plan with throttling
      const weeklyTouchPlan = await requestThrottler.enqueue(
        `weekly-plan-${userId}`,
        async () => {
          const result = await contactIntelligenceEngine.generateTop50Dashboard(userId);
          return result.weeklyTouchPlan;
        },
        { cacheTime: 60000, priority: 1 } // Cache for 1 minute
      );

      const dashboardData: DashboardData = {
        relationships: top50,
        weeklyTouchPlan,
        powerNodes,
        decayingRelationships,
      };

      console.log('🔍 [TOP50 TRACE] Step 10: Setting dashboard state with:', {
        relationships: top50.length,
        weeklyTouchPlan: weeklyTouchPlan.length,
        powerNodes: powerNodes.length,
        decayingRelationships: decayingRelationships.length
      });
      setDashboard(dashboardData);
      
      // Initialize lazy loading with first batch
      const firstBatch = top50.slice(0, contactsPerBatch);
      console.log('🔍 [TOP50 TRACE] Step 11: Setting displayedContacts with first batch:', firstBatch.length);
      setDisplayedContacts(firstBatch);
      setHasMoreContacts(top50.length > contactsPerBatch);
      console.log('🔍 [TOP50 TRACE] Step 12: Dashboard load complete');
      
      toast.success(`Dashboard loaded: ${top50.length} contacts scored`);
    } catch (error: any) {
      console.error('Failed to load Top 50 dashboard:', error);
      
      // User-friendly error message
      if (error?.message?.includes('rate limit') || error?.message?.includes('Rate limit')) {
        setRateLimitWarning(true);
        toast.warning('⏱️ Rate limit reached. Slowing down requests...', {
          duration: 5000,
        });
        // Retry after a longer delay
        setTimeout(() => {
          setRateLimitWarning(false);
          toast.info('🔄 Retrying dashboard load...');
          loadDashboard(userId);
        }, 6000);
      } else {
        toast.error('Failed to load dashboard. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔍 [TOP50 TRACE] Auth effect mounted');
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      console.log('🔍 [TOP50 TRACE] Auth state changed:', {
        isLoading: state.isLoading,
        isAuthenticated: state.isAuthenticated,
        hasUser: !!state.user,
        userId: state.user?.id
      });
      if (state.user) {
        console.log('🔍 [TOP50 TRACE] User authenticated, calling loadDashboard');
        setUser(state.user);
        loadDashboard(state.user.id);
      } else {
        console.log('🔍 [TOP50 TRACE] No user in auth state');
      }
    });

    return () => {
      console.log('🔍 [TOP50 TRACE] Auth effect cleanup');
      unsubscribe();
    };
  }, []);

  const handleRefreshScores = async () => {
    if (!user) return;
    
    setRefreshing(true);
    try {
      toast.info('Processing CI scores for all contacts...');
      
      // Call the CI unified processor
      const token = blink.auth.getToken();
      
      const response = await fetch('https://ben3ou87--ci-unified-processor.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to process CI scores');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Processed ${result.processed} contacts! Top 50: ${result.top50Count}`);
        await loadDashboard(user.id);
      }
    } catch (error) {
      console.error('Failed to refresh scores:', error);
      toast.error('Failed to refresh CI scores');
    } finally {
      setRefreshing(false);
    }
  };

  // Load more contacts for lazy loading
  const handleLoadMore = useCallback(() => {
    if (!dashboard || isLoadingMore || !hasMoreContacts) return;
    
    setIsLoadingMore(true);
    
    // Simulate processing delay for smooth UX
    setTimeout(() => {
      const currentLength = displayedContacts.length;
      const nextBatch = dashboard.relationships.slice(
        currentLength,
        currentLength + contactsPerBatch
      );
      
      setDisplayedContacts(prev => [...prev, ...nextBatch]);
      setHasMoreContacts(
        currentLength + nextBatch.length < dashboard.relationships.length
      );
      setIsLoadingMore(false);
    }, 300);
  }, [dashboard, displayedContacts.length, isLoadingMore, hasMoreContacts, contactsPerBatch]);

  // Infinite scroll hook
  const { observerTarget } = useInfiniteScroll({
    hasMore: hasMoreContacts,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
    threshold: 300
  });

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'A': return 'bg-green-500';
      case 'B': return 'bg-blue-500';
      case 'C': return 'bg-yellow-500';
      case 'D': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const getTierBadgeVariant = (tier: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (tier) {
      case 'A': return 'default';
      case 'B': return 'secondary';
      default: return 'outline';
    }
  };

  console.log('🔍 [TOP50 TRACE] Render: loading =', loading);
  console.log('🔍 [TOP50 TRACE] Render: dashboard =', dashboard ? 'EXISTS' : 'NULL');
  console.log('🔍 [TOP50 TRACE] Render: dashboard.relationships.length =', dashboard?.relationships?.length || 0);
  console.log('🔍 [TOP50 TRACE] Render: displayedContacts.length =', displayedContacts.length);
  console.log('🔍 [TOP50 TRACE] Render: user =', user?.id || 'NO USER');

  if (loading) {
    console.log('🔍 [TOP50 TRACE] Render: Showing loading skeleton');
    return <Top50DashboardSkeleton />;
  }

  if (!dashboard) {
    console.log('🔍 [TOP50 TRACE] Render: No dashboard, showing error');
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto" />
          <p className="text-muted-foreground">Failed to load dashboard</p>
        </div>
      </div>
    );
  }

  console.log('🔍 [TOP50 TRACE] Render: Rendering dashboard with', displayedContacts.length, 'displayed contacts');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-500" />
            Top 50 Relationships
          </h1>
          <p className="text-muted-foreground mt-2">
            Your highest-value relationships ranked by Contact Intelligence (CI) score
          </p>
          {rateLimitWarning && (
            <div className="mt-2 flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
              <AlertTriangle className="w-4 h-4" />
              <span>Loading paused due to rate limits. Retrying automatically...</span>
            </div>
          )}
        </div>
        <Button 
          onClick={handleRefreshScores}
          disabled={refreshing || rateLimitWarning}
          size="lg"
          className="gap-2"
        >
          {refreshing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Updating...
            </>
          ) : rateLimitWarning ? (
            <>
              <AlertTriangle className="w-4 h-4" />
              Rate Limited
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Refresh CI Scores
            </>
          )}
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Top 50 Contacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.relationships.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Tracked relationships</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-500" />
              Power Nodes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.powerNodes.length}</div>
            <p className="text-xs text-muted-foreground mt-1">High-influence contacts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Decaying
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.decayingRelationships.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Need attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-500" />
              This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.weeklyTouchPlan.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Planned touches</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="top50" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="top50">Top 50</TabsTrigger>
          <TabsTrigger value="weekly">Weekly Plan</TabsTrigger>
          <TabsTrigger value="power">Power Nodes</TabsTrigger>
          <TabsTrigger value="decaying">Decaying</TabsTrigger>
        </TabsList>

        {/* Top 50 List */}
        <TabsContent value="top50" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Your Top 50 Relationships</CardTitle>
                  <CardDescription>
                    Ranked by comprehensive CI score (Strength, Relevance, Influence, Responsiveness, Reciprocity)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Filter Controls */}
                  <Button
                    variant={filterReady ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setFilterReady(!filterReady);
                      if (user) loadDashboard(user.id, true);
                    }}
                    className="gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    {filterReady ? 'Ready to Mobilize' : 'All Contacts'}
                  </Button>
                  
                  {/* Sort Controls */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const sortOptions: Array<'ci' | 'motivation' | 'access' | 'suitability'> = ['ci', 'motivation', 'access', 'suitability'];
                      const currentIndex = sortOptions.indexOf(sortBy);
                      const nextSort = sortOptions[(currentIndex + 1) % sortOptions.length];
                      setSortBy(nextSort);
                      if (user) loadDashboard(user.id, true);
                    }}
                    className="gap-2"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    Sort: {sortBy === 'ci' ? 'CI Score' : sortBy === 'motivation' ? 'Motivation' : sortBy === 'access' ? 'Access' : 'Suitability'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-3">
                  {displayedContacts.map((contact, index) => (
                    <Card key={contact.contactId} className="relative overflow-hidden">
                      {/* Rank Badge */}
                      <div className="absolute top-4 left-4">
                        <div className={`w-10 h-10 rounded-full ${getTierColor(contact.ciScore.tier)} flex items-center justify-center text-white font-bold text-sm`}>
                          #{index + 1}
                        </div>
                      </div>

                      <CardContent className="pt-6 pl-20">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>
                                  {contact.name?.charAt(0).toUpperCase() || contact.emails[0]?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-semibold text-lg">
                                  {contact.name || contact.emails[0]}
                                </p>
                              </div>
                            </div>

                            {/* CI Score Breakdown with PAMSSAC */}
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="text-sm">
                                CI: {contact.scores.ci}
                              </Badge>
                              {contact.pamssac && (
                                <div className="relative group">
                                  <Badge variant="secondary" className="text-sm cursor-help">
                                    PAMSSAC: {contact.pamssac.total}
                                  </Badge>
                                  {/* Tooltip on hover */}
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-80 p-3 bg-popover border rounded-lg shadow-lg">
                                    <p className="text-xs font-semibold mb-2">Weakest Dimensions:</p>
                                    <div className="space-y-2">
                                      {contact.pamssac.weakestTwo.map((weak, i) => (
                                        <div key={i} className="text-xs">
                                          <p className="font-medium">{weak.dimension}: {weak.score}/100</p>
                                          <ul className="ml-3 mt-1 text-muted-foreground">
                                            {Array.isArray(weak.evidence) && weak.evidence.map((ev, j) => (
                                              <li key={j}>• {typeof ev === 'object' ? JSON.stringify(ev) : String(ev)}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 pt-2 border-t">
                                      <p className="text-xs font-semibold mb-1">Full Breakdown:</p>
                                      <div className="grid grid-cols-2 gap-1 text-xs">
                                        <div>P: {contact.pamssac.P}</div>
                                        <div>A1: {contact.pamssac.A1}</div>
                                        <div>M: {contact.pamssac.M}</div>
                                        <div>S1: {contact.pamssac.S1}</div>
                                        <div>S2: {contact.pamssac.S2}</div>
                                        <div>A2: {contact.pamssac.A2}</div>
                                        <div>C: {contact.pamssac.C}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-5 gap-2 mt-4">
                              <div>
                                <p className="text-xs text-muted-foreground">Strength</p>
                                <Progress value={contact.scores.strength} className="h-2 mt-1" />
                                <p className="text-xs font-medium mt-1">{contact.scores.strength}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Relevance</p>
                                <Progress value={contact.scores.relevance} className="h-2 mt-1" />
                                <p className="text-xs font-medium mt-1">{contact.scores.relevance}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Influence</p>
                                <Progress value={contact.scores.influence} className="h-2 mt-1" />
                                <p className="text-xs font-medium mt-1">{contact.scores.influence}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Response</p>
                                <Progress value={contact.scores.responsiveness} className="h-2 mt-1" />
                                <p className="text-xs font-medium mt-1">{contact.scores.responsiveness}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Reciprocity</p>
                                <Progress value={contact.scores.reciprocity} className="h-2 mt-1" />
                                <p className="text-xs font-medium mt-1">{contact.scores.reciprocity}</p>
                              </div>
                            </div>

                            {/* Why Now Factors */}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {contact.whyNow.map((factor, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {typeof factor === 'object' ? JSON.stringify(factor) : String(factor)}
                                </Badge>
                              ))}
                            </div>

                            {/* Next Touch Window */}
                            <div className="mt-3 p-3 bg-primary/5 rounded-lg">
                              <p className="text-sm font-medium flex items-center gap-2">
                                <Zap className="w-4 h-4 text-primary" />
                                Next Touch Due:
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {contact.nextTouchDueAt 
                                    ? new Date(contact.nextTouchDueAt) <= new Date()
                                      ? 'Due now'
                                      : `Due ${new Date(contact.nextTouchDueAt).toLocaleDateString()}`
                                    : 'Not scheduled'}
                                </Badge>
                                {contact.lastTouchAt && (
                                  <span className="text-xs text-muted-foreground">
                                    Last: {new Date(contact.lastTouchAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Total CI Score */}
                          <div className="text-center ml-4">
                            <Badge variant={getTierBadgeVariant(contact.scores.tier)} className="mb-2">
                              Tier {contact.scores.tier}
                            </Badge>
                            <div className="text-4xl font-bold text-primary">
                              {contact.scores.ci}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">CI Score</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Infinite Scroll Trigger */}
                  {hasMoreContacts && !isLoadingMore && (
                    <div ref={observerTarget} className="h-4" />
                  )}
                  
                  {/* Loading More Skeleton */}
                  {isLoadingMore && (
                    <div className="space-y-3 pt-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Top50ContactCardSkeleton key={i} />
                      ))}
                    </div>
                  )}
                  
                  {/* End of List Message */}
                  {!hasMoreContacts && displayedContacts.length > 0 && (
                    <div className="text-center py-6 text-muted-foreground">
                      <p className="text-sm">You've reached the end of your top {displayedContacts.length} contacts</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Touch Plan */}
        <TabsContent value="weekly" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                This Week's Touch Plan
              </CardTitle>
              <CardDescription>
                Top 10 actions to maintain your most valuable relationships
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboard.weeklyTouchPlan.map((plan, index) => {
                  const contact = dashboard.relationships.find(c => c.contactId === plan.contactId);
                  if (!contact) return null;

                  return (
                    <Card key={plan.contactId}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">
                            {plan.priority}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <p className="font-semibold">{contact.name || contact.emails[0]}</p>
                              <Badge variant={getTierBadgeVariant(contact.scores.tier)}>
                                Tier {contact.scores.tier}
                              </Badge>
                              <Badge variant="outline" className="ml-auto">
                                CI: {contact.scores.ci}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              {contact.emails[0]}
                            </p>
                            <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg">
                              <ArrowRight className="w-4 h-4 text-primary flex-shrink-0" />
                              <p className="text-sm font-medium">{plan.action.action}</p>
                              <Badge variant="outline" className="text-xs ml-auto">
                                Priority {plan.action.priority}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              {plan.action.reason} • Due {new Date(plan.action.dueBy).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Power Nodes */}
        <TabsContent value="power" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                Power Nodes
              </CardTitle>
              <CardDescription>
                High-value contacts with significant influence and reach
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard.powerNodes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Crown className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No power nodes identified yet</p>
                  <p className="text-sm mt-1">Build relationships with high-influence contacts</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboard.powerNodes.map((contact) => {
                    const daysAgo = contact.lastTouchAt 
                      ? Math.floor((Date.now() - new Date(contact.lastTouchAt).getTime()) / (1000 * 60 * 60 * 24))
                      : null;

                    return (
                      <Card key={contact.contactId} className="border-yellow-200">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-yellow-100 text-yellow-800">
                                    {contact.name?.charAt(0).toUpperCase() || contact.emails[0]?.charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold text-lg">{contact.name || contact.emails[0]}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mt-4">
                                <div>
                                  <p className="text-xs text-muted-foreground">CI Score</p>
                                  <p className="text-2xl font-bold text-primary">{contact.scores.ci}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Influence</p>
                                  <p className="text-2xl font-bold text-yellow-500">{contact.scores.influence}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Strength</p>
                                  <p className="text-2xl font-bold text-green-600">{contact.scores.strength}</p>
                                </div>
                              </div>
                              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                                <p className="text-sm text-muted-foreground text-xs mb-1">Why now:</p>
                                <ul className="text-sm space-y-1">
                                  {contact.whyNow.map((reason, i) => (
                                    <li key={i}>• {typeof reason === 'object' ? JSON.stringify(reason) : String(reason)}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Decaying Relationships */}
        <TabsContent value="decaying" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Decaying Relationships
              </CardTitle>
              <CardDescription>
                High-value relationships that need immediate attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard.decayingRelationships.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="w-16 h-16 mx-auto mb-4 opacity-50 text-green-500" />
                  <p className="text-green-600">All relationships healthy!</p>
                  <p className="text-sm mt-1">No high-value relationships are at risk</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboard.decayingRelationships.map((contact) => {
                    const daysAgo = contact.lastTouchAt 
                      ? Math.floor((Date.now() - new Date(contact.lastTouchAt).getTime()) / (1000 * 60 * 60 * 24))
                      : null;

                    return (
                      <Card key={contact.contactId} className="border-red-200">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-red-100 text-red-800">
                                    {contact.name?.charAt(0).toUpperCase() || contact.emails[0]?.charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold text-lg">{contact.name || contact.emails[0]}</p>
                                  <Badge variant="destructive" className="mt-1">
                                    {daysAgo} days since last contact
                                  </Badge>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mt-4">
                                <div>
                                  <p className="text-xs text-muted-foreground">CI Score</p>
                                  <p className="text-2xl font-bold text-primary">{contact.scores.ci}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Tier</p>
                                  <Badge variant={getTierBadgeVariant(contact.scores.tier)} className="text-lg">
                                    {contact.scores.tier}
                                  </Badge>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Last Touch</p>
                                  <p className="text-sm font-medium">
                                    {contact.lastTouchAt 
                                      ? new Date(contact.lastTouchAt).toLocaleDateString()
                                      : 'Never'}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                                <p className="text-sm font-medium flex items-center gap-2">
                                  <TrendingDown className="w-4 h-4 text-red-500" />
                                  Urgent Action:
                                </p>
                                <ul className="text-sm mt-2 space-y-1">
                                  {contact.whyNow.map((reason, i) => (
                                    <li key={i} className="text-xs">• {typeof reason === 'object' ? JSON.stringify(reason) : String(reason)}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
